import { useState, useEffect } from 'react';
import { X, Calendar, Search } from 'lucide-react';

export default function ChangelogModal({ isOpen, onClose }) {
  const [changelog, setChangelog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setSearchQuery(''); // Reset search when modal opens
      fetch('/changelog.json')
        .then(res => {
          if (!res.ok) {
            return { entries: [] };
          }
          return res.json();
        })
        .then(data => {
          setChangelog(data);
          setLoading(false);

          // Mark as seen
          if (data && data.version) {
            localStorage.setItem('lastSeenChangelogVersion', data.version);
          }
        })
        .catch(err => {
          console.error('Failed to load changelog:', err);
          setChangelog({ entries: [] });
          setLoading(false);
        });
    }
  }, [isOpen]);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Filter entries based on search query
  const filteredEntries = changelog?.entries?.filter(entry => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      entry.message.toLowerCase().includes(query) ||
      entry.type.toLowerCase().includes(query)
    );
  }) || [];

  const badgeStyles = {
    feature: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    fix: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    improvement: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    breaking: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
  };

  const badgeEmoji = {
    feature: '🎉',
    fix: '🐛',
    improvement: '⚡',
    breaking: '⚠️'
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              What's New
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Search */}
          <div className="px-6 pt-4 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search updates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {searchQuery && !loading && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {filteredEntries.length} {filteredEntries.length === 1 ? 'result' : 'results'} found
              </p>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 dark:border-blue-400 border-t-transparent"></div>
              </div>
            ) : filteredEntries.length > 0 ? (
              <div className="space-y-4">
                {filteredEntries.map((entry, index) => (
                  <div
                    key={index}
                    className="border-l-4 border-gray-200 dark:border-gray-700 pl-4 py-2 hover:border-blue-400 dark:hover:border-blue-600 transition-colors"
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${badgeStyles[entry.type]} flex-shrink-0`}>
                        {badgeEmoji[entry.type]} {entry.type}
                      </span>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="h-3 w-3" />
                        {formatDate(entry.date)}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {entry.message}
                    </p>
                    {entry.version && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        v{entry.version}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">
                  {searchQuery ? `No updates found matching "${searchQuery}"` : 'No changelog entries yet.'}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              VerbaGPT {changelog?.version ? `v${changelog.version}` : ''}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
